import React, { useEffect, useContext } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@chargify/formatters';

import { useServices } from '../../hooks';
import { ConfigContext } from '../../contexts';
import { INITIAL_PAGE } from './constants';

import InvoicesTable from '../../components/InvoicesTable';
import {
  defaultSortingParams,
  billingHistorySelectors,
} from '../../machines/billingHistoryData';
import { SortDir } from '../../models/billingHistory/billingHistory';

const BillingHistoryContainer = () => {
  const { i18n } = useTranslation();
  const { billingHistoryDataService, filtersOptionsService } = useServices();
  const { billingHistory } = useContext(ConfigContext);
  const { itemsPerPage, paginationVariant } = billingHistory;

  useEffect(() => {
    if (billingHistoryDataService.getSnapshot().matches('machineStatus.idle')) {
      billingHistoryDataService.send('INITIALIZE', {
        page: INITIAL_PAGE,
        limit: itemsPerPage,
      });
    }

    if (filtersOptionsService.getSnapshot().matches('idle')) {
      filtersOptionsService.send('INITIALIZE');
    }
  }, []);

  const { items, sorting, pagination } = useSelector(
    billingHistoryDataService,
    billingHistorySelectors.getBillingHistoryData
  );

  const { isLoading, isFiltering } = useSelector(
    billingHistoryDataService,
    billingHistorySelectors.getBillingHistoryState
  );

  const sendSortEvent = (sortBy: string, sortDirection: SortDir) => {
    billingHistoryDataService.send('SORT_BY', {
      sortBy,
      sortDirection,
    });
  };

  const onSortChange = (columnId: string) => {
    if (
      sorting.sortBy === defaultSortingParams.sortBy &&
      sorting.sortDirection === 'desc'
    ) {
      return sendSortEvent(columnId, 'asc');
    }

    if (sorting.sortBy === columnId) {
      if (sorting.sortDirection === 'desc') {
        sendSortEvent(
          defaultSortingParams.sortBy,
          defaultSortingParams.sortDirection
        );
      } else {
        sendSortEvent(columnId, 'desc');
      }
    } else {
      sendSortEvent(columnId, 'asc');
    }
  };

  const onPageChange = (page: number) => {
    billingHistoryDataService.send('CHANGE_PAGE', {
      page,
    });
  };

  const onLoadMore = () => {
    billingHistoryDataService.send('LOAD_MORE', {
      limit: itemsPerPage,
    });
  };

  const onItemsPerPageChange = (itemsPerPage: number) => {
    billingHistoryDataService.send('CHANGE_ITEMS_PER_PAGE', { itemsPerPage });
  };

  const formattedData = items?.map(
    ({ currency, amountDueInCents, totalAmountInCents, ...rest }) => ({
      ...rest,
      amountDue: formatCurrency(currency, amountDueInCents, i18n.language),
      totalAmount: formatCurrency(currency, totalAmountInCents, i18n.language),
    })
  );

  return (
    <InvoicesTable
      isFiltering={isFiltering}
      isLoading={isLoading}
      totalPages={pagination?.totalPages}
      onLoadMore={onLoadMore}
      totalInvoices={pagination?.totalItems}
      paginationVariant={paginationVariant}
      page={sorting.page}
      onPageChange={onPageChange}
      invoices={formattedData || []}
      onSortChange={onSortChange}
      sortMode={sorting}
      itemsPerPage={pagination?.itemsPerPage}
      onItemsPerPageChange={onItemsPerPageChange}
    />
  );
};
export default BillingHistoryContainer;
